// The gifting-moment / video-review uploader; limits mirror supabase/functions/ugc-submissions/rules.ts in Mobius, which has the final say.
(function () {
  var MAX_FILES = 3;
  var MAX_BYTES = 125 * 1024 * 1024;
  var UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
  var TYPES = {
    mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif', webp: 'image/webp'
  };
  var ALLOWED = Object.keys(TYPES).map(function (k) { return TYPES[k]; });
  var OFFLINE = 'We lost the connection. Please check your signal and try again.';
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var scrollBehaviour = reduceMotion ? 'auto' : 'smooth';

  // Phones often send no type for HEIC/MOV, so fall back to the extension.
  function fileType(file) {
    var type = (file.type || '').toLowerCase();
    if (ALLOWED.indexOf(type) !== -1) return type;
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    return TYPES[ext] || null;
  }

  function formatSize(bytes) {
    return bytes >= 1024 * 1024 ? (bytes / 1024 / 1024).toFixed(1) + ' MB' : Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  function post(endpoint, body) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).catch(function () {
      throw new Error(OFFLINE);
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var err = new Error(data.error || 'Sorry, something went wrong. Please try again.');
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  function upload(url, file, type, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var fail = function () { reject(new Error(OFFLINE)); };
      xhr.upload.addEventListener('progress', function (e) {
        if (e.lengthComputable) onProgress(e.loaded);
      });
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(file.name + " didn't upload. Please try again."));
      });
      xhr.addEventListener('error', fail);
      xhr.addEventListener('abort', fail);
      xhr.addEventListener('timeout', fail);
      xhr.open('PUT', url, true);
      xhr.timeout = UPLOAD_TIMEOUT_MS;
      xhr.setRequestHeader('Content-Type', type);
      xhr.send(file);
    });
  }

  function previewUrl(file) {
    try { return window.URL && URL.createObjectURL ? URL.createObjectURL(file) : ''; } catch (e) { return ''; }
  }

  function revoke(url) {
    if (url && window.URL && URL.revokeObjectURL) URL.revokeObjectURL(url);
  }

  function initForm(form) {
    if (form.dataset.ugcReady) return;
    form.dataset.ugcReady = 'true';

    var endpoint = form.dataset.endpoint;
    var region = form.dataset.region;
    var source = form.dataset.source || 'gifting_moment';
    var input = form.querySelector('[data-ugc-files]');
    var drop = form.querySelector('[data-ugc-drop]');
    var list = form.querySelector('[data-ugc-file-list]');
    var pick = form.querySelector('[data-gm-pick]');
    var details = form.querySelector('[data-gm-details]');
    var submitRow = form.querySelector('[data-gm-submit-row]');
    var errorBox = form.querySelector('[data-ugc-error]');
    var progress = form.querySelector('[data-ugc-progress]');
    var bar = form.querySelector('[data-ugc-progress-bar]');
    var progressText = form.querySelector('[data-ugc-progress-text]');
    var submit = form.querySelector('[data-ugc-submit]');
    var success = form.parentElement.querySelector('[data-ugc-success]');
    var consentText = form.querySelector('[data-ugc-consent-text]');
    // { file, url } per pick; the preview URL lives as long as the pick.
    var picks = [];
    var busy = false;
    // The started submission and which files have arrived, so a retry only resends what's missing.
    var started = null;
    var invalid = null;

    function showError(message, field) {
      if (invalid) invalid.removeAttribute('aria-invalid');
      invalid = field || null;
      errorBox.textContent = message;
      errorBox.hidden = !message;
      if (invalid) {
        invalid.setAttribute('aria-invalid', 'true');
        invalid.focus();
      } else if (message && errorBox.scrollIntoView) {
        errorBox.scrollIntoView({ block: 'center', behavior: scrollBehaviour });
      }
    }

    form.addEventListener('input', function (e) {
      if (e.target === invalid) e.target.removeAttribute('aria-invalid');
      // Edited details go in a fresh submission; the unfinished one doesn't count against them.
      if (!busy && e.target !== input) started = null;
    });

    function preview(pickItem, i) {
      var item = document.createElement('div');
      item.className = 'relative w-[104px] shrink-0 pt-2';
      var frame = document.createElement('div');
      frame.className = 'relative aspect-[9/16] overflow-hidden rounded-lg border border-solid border-grey-150 bg-grey-900';
      if (pickItem.url && fileType(pickItem.file).indexOf('video/') === 0) {
        var video = document.createElement('video');
        video.className = 'absolute inset-0 h-full w-full object-cover';
        video.muted = true;
        video.loop = true;
        video.autoplay = !reduceMotion;
        video.setAttribute('playsinline', '');
        video.setAttribute('aria-hidden', 'true');
        video.src = pickItem.url;
        frame.appendChild(video);
      } else if (pickItem.url) {
        var img = document.createElement('img');
        img.className = 'absolute inset-0 h-full w-full object-cover !rounded-none';
        img.alt = '';
        img.src = pickItem.url;
        frame.appendChild(img);
      }
      var size = document.createElement('span');
      size.className = 'absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-grey-900/80 to-transparent px-2 pb-1.5 pt-5 text-2xs font-semibold text-white';
      size.textContent = formatSize(pickItem.file.size);
      frame.appendChild(size);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'absolute -right-1.5 top-0.5 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-solid border-grey-150 bg-white p-0 text-base leading-none text-grey-900 shadow-sm';
      remove.textContent = '×';
      remove.setAttribute('aria-label', 'Remove ' + pickItem.file.name);
      remove.setAttribute('data-gm-remove', '');
      remove.addEventListener('click', function () {
        if (busy) return;
        revoke(pickItem.url);
        picks.splice(i, 1);
        started = null;
        renderFiles();
        var next = list.querySelectorAll('[data-gm-remove]')[Math.min(i, picks.length - 1)];
        (next || input).focus();
      });

      item.appendChild(frame);
      item.appendChild(remove);
      return item;
    }

    function renderFiles() {
      list.innerHTML = '';
      picks.forEach(function (pickItem, i) { list.appendChild(preview(pickItem, i)); });

      if (picks.length && picks.length < MAX_FILES) {
        var more = document.createElement('button');
        more.type = 'button';
        more.setAttribute('data-gm-add-more', '');
        more.className = 'mt-2 flex aspect-[9/16] w-[104px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-grey-300 bg-based p-0 text-center font-body text-sm text-grey-800 hover:border-grey-900';
        more.innerHTML = '<span class="text-2xl leading-none" aria-hidden="true">+</span><span>Add another</span>';
        more.addEventListener('click', function () { input.click(); });
        list.appendChild(more);
      }

      var has = picks.length > 0;
      input.tabIndex = has ? -1 : 0;
      pick.hidden = has;
      details.hidden = !has;
      submitRow.hidden = !has;
    }

    function addFiles(picked) {
      var problem = '';
      var hadNone = picks.length === 0;
      Array.prototype.forEach.call(picked, function (file) {
        if (!fileType(file)) problem = file.name + " isn't a video or photo we can take (MP4, MOV, JPG, PNG or HEIC).";
        else if (file.size > MAX_BYTES) problem = file.name + ' is over 125 MB. Please trim it or send a shorter clip.';
        else if (picks.length >= MAX_FILES) problem = 'Please send up to ' + MAX_FILES + ' files.';
        else picks.push({ file: file, url: previewUrl(file) });
      });
      started = null;
      renderFiles();
      showError(problem);
      if (!problem && hadNone && picks.length) {
        var first = form.querySelector('[name="name"]');
        if (first) first.focus();
      } else if (!hadNone) {
        // The focused "Add another" was rebuilt, so put focus back on its replacement (or the last remove).
        var again = list.querySelector('[data-gm-add-more]') || list.querySelectorAll('[data-gm-remove]')[picks.length - 1];
        if (again) again.focus();
      }
    }

    input.addEventListener('change', function () {
      addFiles(input.files);
      input.value = '';
    });

    ['dragover', 'dragenter'].forEach(function (name) {
      drop.addEventListener(name, function (e) {
        e.preventDefault();
        drop.classList.add('is-dragging');
      });
    });
    ['dragleave', 'drop'].forEach(function (name) {
      drop.addEventListener(name, function () { drop.classList.remove('is-dragging'); });
    });
    drop.addEventListener('drop', function (e) {
      e.preventDefault();
      if (!busy && e.dataTransfer) addFiles(e.dataTransfer.files);
    });

    // Details are locked while uploading: the started submission already holds them.
    function setBusy(on) {
      busy = on;
      submit.disabled = on;
      input.disabled = on;
      form.querySelectorAll('[data-gm-details] input, [data-gm-details] textarea, [data-gm-add-more], [data-gm-remove]').forEach(function (el) {
        el.disabled = on;
      });
      progress.hidden = !on;
    }

    function setProgress(fraction, text) {
      bar.style.width = Math.round(fraction * 100) + '%';
      progressText.textContent = text;
    }

    function consentWording() {
      return (consentText.innerText || consentText.textContent).replace(/[ \t]+/g, ' ').trim();
    }

    // The started submission's uploads that are still to send: all of them first, only the missing ones on a retry.
    function pending(data) {
      if (!started) {
        return post(endpoint, Object.assign({ action: 'start' }, data)).then(function (start) {
          started = { id: start.id, paths: start.uploads.map(function (u) { return u.path; }) };
          return start.uploads;
        });
      }
      return post(endpoint, { action: 'resume', id: started.id }).then(function (resume) {
        return resume.uploads;
      }, function (err) {
        if (err.status === 404 || err.status === 410) started = null;
        throw err;
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;
      showError('');

      var data = new FormData(form);
      var field = function (name) { return String(data.get(name) || '').trim(); };
      var el = function (name) { return form.querySelector('[name="' + name + '"]'); };
      if (!picks.length) return showError('Please add a video or photo.', input);
      if (!field('name')) return showError('Please tell us your name.', el('name'));
      if (!/^[^\s@?&=,;<>"'()]+@[^\s@?&=,;<>"'()]+\.[^\s@?&=,;<>"'()]+$/.test(field('email'))) return showError('Please enter a valid email address.', el('email'));
      if (!field('story')) return showError('Please tell us a little about your gift.', el('story'));
      var questions = Array.prototype.slice.call(form.querySelectorAll('[data-gm-question]'));
      var answers = questions.map(function (q) {
        return {
          question: q.querySelector('[data-gm-question-label]').textContent.trim(),
          answer: q.querySelector('[data-gm-question-answer]').value.trim()
        };
      });
      for (var a = 0; a < answers.length; a++) {
        if (!answers[a].answer) return showError('Please answer: ' + answers[a].question, questions[a].querySelector('[data-gm-question-answer]'));
      }
      if (!el('consent').checked) return showError('Please tick the box to let us share your video.', el('consent'));

      setBusy(true);
      setProgress(0, 'Getting ready…');

      pending({
        region: region,
        source: source,
        answers: answers,
        name: field('name'),
        email: field('email'),
        gift: field('gift'),
        story: field('story'),
        social_handle: field('social_handle'),
        website: field('website'),
        consent: true,
        consent_text: consentWording(),
        page_url: window.location.origin + window.location.pathname,
        files: picks.map(function (p) { return { name: p.file.name, type: fileType(p.file), size: p.file.size }; })
      })
        .then(function (uploads) {
          var todo = uploads.map(function (u) {
            return { url: u.url, file: picks[started.paths.indexOf(u.path)].file };
          });
          var total = todo.reduce(function (sum, t) { return sum + t.file.size; }, 0) || 1;
          var done = 0;
          return todo.reduce(function (chain, t, i) {
            return chain.then(function () {
              var label = todo.length > 1 ? 'Uploading ' + (i + 1) + ' of ' + todo.length : 'Uploading';
              return upload(t.url, t.file, fileType(t.file), function (loaded) {
                var fraction = (done + loaded) / total;
                setProgress(fraction, label + '… ' + Math.round(fraction * 100) + '%');
              }).then(function () { done += t.file.size; });
            });
          }, Promise.resolve());
        })
        .then(function () {
          setProgress(1, 'Nearly there…');
          return post(endpoint, { action: 'finish', id: started.id });
        })
        .then(function () {
          picks.forEach(function (p) { revoke(p.url); });
          picks = [];
          list.innerHTML = '';
          form.hidden = true;
          success.hidden = false;
          success.focus();
          if (success.scrollIntoView) success.scrollIntoView({ block: 'center', behavior: scrollBehaviour });
        })
        .catch(function (err) {
          setBusy(false);
          showError(err.message || 'Sorry, something went wrong. Please try again.');
        });
    });
  }

  function initPage(root) {
    if (root.dataset.gmReady) return;
    root.dataset.gmReady = 'true';
    if (!('IntersectionObserver' in window)) return;

    var reels = root.querySelectorAll('[data-gm-reel]');
    if (reels.length && !reduceMotion) {
      var reelWatch = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var played = video.play();
            if (played && played.catch) played.catch(function () {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.25 });
      reels.forEach(function (v) { reelWatch.observe(v); });
    }

    // The pill shows whenever neither the hero button nor the uploader is on screen.
    var sticky = root.querySelector('[data-gm-sticky]');
    var targets = Array.prototype.slice.call(root.querySelectorAll('[data-gm-hero-cta], [data-gm-card]'));
    if (sticky && targets.length) {
      var onScreen = [];
      var watch = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var i = onScreen.indexOf(entry.target);
          if (entry.isIntersecting && i === -1) onScreen.push(entry.target);
          if (!entry.isIntersecting && i !== -1) onScreen.splice(i, 1);
        });
        sticky.hidden = onScreen.length > 0;
      });
      targets.forEach(function (t) { watch.observe(t); });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-ugc-form]').forEach(initForm);
    document.querySelectorAll('.section-gifting-moment').forEach(initPage);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
